import { useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Alert, Grid, Divider } from '@mui/material';
import { Building2, Star } from 'lucide-react';
import { useTenant, usePortfolioCompanies, useFunds } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { useQuery } from '@tanstack/react-query';
import { createClient } from '@lingaai/supabase-client';
import { TIME_SAVINGS } from '@lingaai/shared-utils';

const supabase = createClient();
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

export function TenantInfo() {
  const { data: user } = useCurrentUser();
  const { data: tenant, isLoading } = useTenant(user?.tenant_id ?? '');
  const { data: portfolioCompanies } = usePortfolioCompanies();
  const { data: funds } = useFunds();
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

  const portcoCount = portfolioCompanies?.length || 0;

  // Calculate portco counts by frequency
  const frequencyBreakdown =
    funds?.reduce(
      (acc, fund) => {
        const fundPortcos = portfolioCompanies?.filter((pc) => pc.fund_id === fund.id) || [];
        const frequency = fund.reporting_frequency || 'quarterly';
        acc[frequency] = (acc[frequency] || 0) + fundPortcos.length;
        return acc;
      },
      {} as Record<string, number>
    ) || {};

  const hasMonthly = (frequencyBreakdown['monthly'] || 0) > 0;
  const hasQuarterly = (frequencyBreakdown['quarterly'] || 0) > 0;
  const hasMixedFrequencies = hasMonthly && hasQuarterly;

  // Fetch available prices
  const { data: prices } = useQuery({
    queryKey: ['prices'],
    queryFn: async () => {
      const { data } = await supabase.from('prices').select('*, product:products(*)').eq('active', true).order('unit_amount');
      return data;
    },
  });

  // Fetch user's subscription
  const { data: subscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.from('subscriptions').select('*').eq('user_id', user.id).eq('status', 'active').single();
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch employee stats (only when subscribed)
  const { data: employeeStats } = useQuery({
    queryKey: ['employee-stats', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return null;

      const [metricsResult, submissionsResult] = await Promise.all([
        supabase.from('metrics').select('id', { count: 'exact', head: true }).eq('tenant_id', tenant.id),
        supabase.from('submissions').select('id', { count: 'exact', head: true }).eq('tenant_id', tenant.id),
      ]);

      const metricsCount = metricsResult.count || 0;
      const submissionsCount = submissionsResult.count || 0;
      const companiesCount = portfolioCompanies?.length || 0;
      const hoursSaved = Math.round(companiesCount * TIME_SAVINGS.HOURS_PER_COMPANY_PER_PERIOD);

      return {
        metricsExtracted: metricsCount,
        submissionsProcessed: submissionsCount,
        hoursSaved,
      };
    },
    enabled: !!subscription && !!tenant?.id,
  });

  // Handle checkout for a specific frequency
  const handleCheckout = async (frequency: 'monthly' | 'quarterly') => {
    if (!user) return;

    setIsLoadingCheckout(true);

    try {
      // Find the appropriate price
      const selectedPrice = prices?.find((p) => p.interval === 'month' && p.interval_count === (frequency === 'monthly' ? 1 : 3));

      if (!selectedPrice) {
        throw new Error('Price not found');
      }

      // Create checkout session
      const response = await fetch(`${import.meta.env.VITE_API_URL}/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          priceId: selectedPrice.id,
          returnUrl: window.location.href,
        }),
      });

      const data = await response.json();
      console.log('Checkout response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Checkout failed');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  // Handle billing portal
  const handleManageBilling = async () => {
    if (!user) return;

    setIsLoadingCheckout(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/billing/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          returnUrl: window.location.href,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      // Redirect to Stripe Portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Billing portal error:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading tenant information...</Typography>
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No tenant information available.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Tenant Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Building2 size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            <Typography variant="h6">Tenant Information</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Organization Name
              </Typography>
              <Typography variant="body1">{tenant.name}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Workspace Slug
              </Typography>
              <Typography variant="body1">{tenant.workspace_slug}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Workspace Email
              </Typography>
              <Typography variant="body1">{tenant.workspace_email}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Current Employees or Available to Hire */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {subscription ? 'Current Employees' : 'Available to Hire'}
      </Typography>

      <Box sx={{ maxWidth: '50%' }}>
        <Card sx={{ mb: 4, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">Portfolio Operations Analyst</Typography>
              <Chip label={subscription ? 'HIRED' : 'Available Now'} size="small" color={subscription ? 'success' : 'primary'} />
            </Box>

            {!subscription && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  AI employee to extract financial and operational metrics from portfolio company documents
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">• Eliminates tedious manual data extraction</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">• Handles the repetitive follow-up work</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">• Reads PDFs, Excel files, and emails automatically</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">• Ensures consistent, complete data collection</Typography>
                  </Grid>
                </Grid>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Show subscription details if hired, otherwise show pricing */}
            {subscription ? (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {/* Left side - Details */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Hired On
                      </Typography>
                      <Typography variant="body1">
                        {new Date(subscription.created).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Covering
                      </Typography>
                      <Typography variant="body1">
                        {subscription.quantity} portfolio {subscription.quantity === 1 ? 'company' : 'companies'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Next Billing
                      </Typography>
                      <Typography variant="body1">
                        {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Box>

                    <Button variant="outlined" size="medium" onClick={handleManageBilling} disabled={isLoadingCheckout} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                      {isLoadingCheckout ? 'Loading...' : 'Manage Billing'}
                    </Button>
                  </Box>

                  {/* Right side - Stats */}
                  {employeeStats && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 200 }}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                          {employeeStats.metricsExtracted.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Metrics Extracted
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                          {employeeStats.submissionsProcessed.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Submissions Processed
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                          ~{employeeStats.hoursSaved}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Hours Saved This Quarter
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : !hasMixedFrequencies ? (
              // Single frequency
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Based on{' '}
                  <strong>
                    {portcoCount} portfolio {portcoCount === 1 ? 'company' : 'companies'}
                  </strong>
                </Typography>

                {hasMonthly && (
                  <>
                    <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700 }}>
                      ${32 * (frequencyBreakdown['monthly'] || 0)}
                      <Typography component="span" variant="body1" color="text.secondary">
                        /month
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      $32 per company per month
                    </Typography>
                    <Button variant="contained" size="large" fullWidth onClick={() => handleCheckout('monthly')} disabled={isLoadingCheckout || !prices}>
                      {isLoadingCheckout ? 'Loading...' : 'Subscribe Monthly'}
                    </Button>
                  </>
                )}

                {hasQuarterly && (
                  <>
                    <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700 }}>
                      ${96 * (frequencyBreakdown['quarterly'] || 0)}
                      <Typography component="span" variant="body1" color="text.secondary">
                        /quarter
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      $96 per company per quarter
                    </Typography>
                    <Button variant="contained" size="large" fullWidth onClick={() => handleCheckout('quarterly')} disabled={isLoadingCheckout || !prices}>
                      {isLoadingCheckout ? 'Loading...' : 'Subscribe Quarterly'}
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              // Mixed frequencies - show both
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>You have funds with different reporting frequencies.</strong>
                  </Typography>
                  <Typography variant="body2">
                    You'll need to subscribe to both monthly and quarterly plans separately. Each subscription will cover the companies in funds with that reporting frequency.
                  </Typography>
                </Alert>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Based on <strong>{portcoCount} portfolio companies</strong>:
                  <br />• <strong>{funds?.filter((f) => f.reporting_frequency === 'monthly').length || 0} funds</strong> with monthly reporting ({frequencyBreakdown['monthly']} companies)
                  <br />• <strong>{funds?.filter((f) => f.reporting_frequency === 'quarterly').length || 0} funds</strong> with quarterly reporting ({frequencyBreakdown['quarterly']} companies)
                </Typography>

                {/* Monthly Subscription */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Monthly Reporting Subscription
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {frequencyBreakdown['monthly']} companies across {funds?.filter((f) => f.reporting_frequency === 'monthly').length || 0}{' '}
                    {funds?.filter((f) => f.reporting_frequency === 'monthly').length === 1 ? 'fund' : 'funds'}
                  </Typography>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700, mb: 2 }}>
                    ${32 * (frequencyBreakdown['monthly'] || 0)}
                    <Typography component="span" variant="body1" color="text.secondary">
                      /month
                    </Typography>
                  </Typography>
                  <Button variant="contained" size="medium" fullWidth onClick={() => handleCheckout('monthly')} disabled={isLoadingCheckout || !prices}>
                    {isLoadingCheckout ? 'Loading...' : 'Subscribe to Monthly'}
                  </Button>
                </Box>

                {/* Quarterly Subscription */}
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Quarterly Reporting Subscription
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {frequencyBreakdown['quarterly']} companies across {funds?.filter((f) => f.reporting_frequency === 'quarterly').length || 0}{' '}
                    {funds?.filter((f) => f.reporting_frequency === 'quarterly').length === 1 ? 'fund' : 'funds'}
                  </Typography>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700, mb: 2 }}>
                    ${96 * (frequencyBreakdown['quarterly'] || 0)}
                    <Typography component="span" variant="body1" color="text.secondary">
                      /quarter
                    </Typography>
                  </Typography>
                  <Button variant="contained" size="medium" fullWidth onClick={() => handleCheckout('quarterly')} disabled={isLoadingCheckout || !prices}>
                    {isLoadingCheckout ? 'Loading...' : 'Subscribe to Quarterly'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Coming Soon */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Coming Soon
      </Typography>

      <Box sx={{ maxWidth: 1200 }}>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* Fund Operations Manager */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'grey.300', opacity: 0.8 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  {/* <Bot size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} color="#757575" /> */}
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      Fund Operations Manager
                    </Typography>
                    <Chip label="Coming Soon" size="small" color="default" sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Streamline fund operations and LP reporting
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Manages capital calls/distributions
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Calculates fund-level performance metrics
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Handles LP reporting preparation
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Tracks fund expenses and fees
                  </Typography>
                </Box>
                <Button variant="outlined" size="medium" fullWidth startIcon={<Star size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}>
                  Show Interest
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Portfolio Engagement Manager */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'grey.300', opacity: 0.8 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  {/* <Bot size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} color="#757575" /> */}
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      Portfolio Engagement Manager
                    </Typography>
                    <Chip label="Coming Soon" size="small" color="default" sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Enhance portfolio company engagement and monitoring
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Collects monthly updates from portfolio companies
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Tracks burn rate and runway alerts
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Monitors hiring and operational changes
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Manages founder communication
                  </Typography>
                </Box>
                <Button variant="outlined" size="medium" fullWidth startIcon={<Star size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}>
                  Show Interest
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Custom AI Employee Request */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'grey.300', opacity: 0.8 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  {/* <Bot size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} color="#757575" /> */}
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      Custom AI Employee
                    </Typography>
                    <Chip label="Request" size="small" color="default" sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Need a specialized AI employee for your unique workflow?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Tailored to your specific needs
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Custom workflows and integrations
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Designed for your unique processes
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • Built with your team's input
                  </Typography>
                </Box>
                <Button variant="outlined" size="medium" fullWidth startIcon={<Star size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}>
                  Request Custom
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Add-ons */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Add-ons
      </Typography>

      <Box sx={{ maxWidth: 1200 }}>
        {/* Custom Email Domain Add-on */}
        <Card sx={{ border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* <Mail size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} color="#1976d2" /> */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">Custom Email Domain</Typography>
                  <Chip label="Add-on" size="small" color="primary" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use your own domain for professional branding
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2">
                    • Send & receive from <strong>data@your-company.com</strong>
                  </Typography>
                  <Typography variant="body2">• Professional brand identity</Typography>
                  <Typography variant="body2">• Full DNS control</Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 150 }}>
                <Typography variant="h5" color="primary.main" sx={{ mb: 1 }}>
                  $199
                  <Typography component="span" variant="body2" color="text.secondary">
                    /mo
                  </Typography>
                </Typography>
                <Button variant="contained" size="large" startIcon={<Star size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />} fullWidth disabled>
                  I'm Interested
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
